import React, { useState, useEffect } from "react";
import {
  Layers,
  AlertTriangle,
  CircleDot,
  FolderKanban,
  User,
  Mail,
  UserCheck,
  UserPlus,
  Timer,
  CalendarDays,
  X,
  ChevronDown,
  History,
  Pencil,
} from "lucide-react";

import frameBg from "../../Assets/FrameBGDashboard.png";
import {
  getTicketHistory,
  ReassignTicketProject,
} from "../../Services/TicketService";
import AttachmentPreviewModal from "../Task/Modal/AttachmentPreviewModal";
import { getImageUrl } from "../Utils/urlHelper";
import { getProjectData } from "../../Services/ProjectService";
import NotificationAlert from "../Alerts/NotificationAlert";
//  import { useNavigate } from "react-router-dom";

const ViewTicketModal = ({
  show,
  onClose,
  onOpenHistory,
  ticket,
  onAssign,
  onReject,
  onApprove,
  onHold,
  onInProgress,
  onReadyForReview,
  onAssignToReviewer,
  onReviewInProgress,
  onReviewed,
  onReopen,
  onClosed,
  onResume,
}) => {
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [showProjectEdit, setShowProjectEdit] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (show) {
      loadProjects();
      setSelectedProject(ticket?.projectUID || "");
    }
  }, [show]);

  if (!show || !ticket) return null;
  const btn =
    "px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition";

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";

    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadHistory = async () => {
    try {
      const res = await getTicketHistory(ticket.ticketNumber);
      if (res.data.status === "Success") {
        const history = res.data.history || [];

        onClose();
        onOpenHistory(history);
      }
    } catch (err) {
      // if (err.response?.status === 400) {
      //   navigate("/bad-request", {
      //     state: {
      //       error: err.response?.data?.message || "Bad request (400)",
      //     },
      //   });
      //   return;
      // }
      console.error("History Load Error:", err);
    }
  };

  const loadProjects = async () => {
    try {
      const isAdminPath = window.location.pathname.includes("/Admin");

      const pmCount = document.cookie
        .split("; ")
        .find((row) => row.startsWith("Orbit_PMCount="))
        ?.split("=")[1];

      const pmCountNumber = Number(pmCount || 0);

      const canEdit = isAdminPath || pmCountNumber > 0;

      if (!canEdit) return;
      const response = await getProjectData(true);

      setProjects(response.data?.data || []);
    } catch (err) {
      console.error("Error loading projects", err);
    }
  };

  const handleProjectUpdate = async () => {
    try {
      const payload = {
        ticketUID: ticket.ticketUID,
        projectUID: selectedProject,
      };

      const res = await ReassignTicketProject(payload);

      if (res.data.status === "Success") {
        setAlert({
          type: "success",
          message: "Project updated successfully",
        });

        const updatedProject = projects.find(
          (p) => p.projectUID === selectedProject,
        );

        if (updatedProject) {
          ticket.projectName = updatedProject.projectName;
          ticket.projectUID = updatedProject.projectUID;
        }

        setShowProjectEdit(false);
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: "Failed to update project",
      });
      console.error(err);
    }
  };
  const isAdminPath = window.location.pathname.includes("/Admin");

  const pmCount = document.cookie
    .split("; ")
    .find((row) => row.startsWith("Orbit_PMCount="))
    ?.split("=")[1];

  const pmCountNumber = Number(pmCount || 0);

  const canEditProject = isAdminPath || (!isAdminPath && pmCountNumber > 0);
  return (
    <>
      {/* MAIN VIEW TICKET MODAL */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onClick={onClose}
      >
        <div
          className="bg-[#F5FAFC] w-full max-w-xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          {/* <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 z-20"
          >
            <X size={26} />
          </button> */}

          {/* Header */}
          <div
            className="p-5 text-white shadow-md bg-cover bg-center bg-no-repeat flex justify-between items-center"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div>
              <h2 className="text-xl font-semibold tracking-wide">
                Ticket Details
              </h2>
              <p className="opacity-90 mt-1">
                <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium">
                  #{ticket.ticketNumber}
                </span>
              </p>
            </div>

            <button
              onClick={loadHistory}
              className="bg-white/20 backdrop-blur px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-white/30 transition text-white shadow"
            >
              <History size={18} /> View History
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Title */}
            <div className="border-b pb-4">
              <p className="text-xl font-semibold text-gray-900">
                {ticket.title}
              </p>
            </div>

            {/* Description */}
            <div className="border-b pb-4">
              <p className="text-gray-700 whitespace-pre-line">
                {ticket.description}
              </p>
            </div>

            {/* LOGO*/}
            {typeof ticket.logoURL === "string" &&
              ticket.logoURL.trim() !== "" && (
                <div className="flex justify-start mt-2 mb-2">
                  <img
                    src={getImageUrl(ticket.logoURL)}
                    alt="Project Logo"
                    className="w-36 h-12 object-contain rounded-lg border bg-white p-1 shadow"
                  />
                </div>
              )}

            {/* GRID DETAILS */}
            <div className="grid grid-cols-2 gap-6">
              <DetailCard
                label="Ticket Type"
                value={ticket.ticketType}
                icon={Layers}
                chip
              />

              <DetailCard
                label="Severity"
                value={ticket.severity}
                icon={AlertTriangle}
                severityChip
              />

              <DetailCard
                label="Status"
                value={ticket.status}
                icon={CircleDot}
              />

              <div className="p-4 bg-gray-50 border rounded-xl shadow-sm">
                <p className="text-gray-500 text-sm mb-1 flex items-center gap-2">
                  <FolderKanban size={16} className="text-[#0061A4]" />
                  Project Name
                </p>

                {!showProjectEdit ? (
                  <div className="flex justify-between items-center">
                    <p className="text-gray-800 font-medium">
                      {ticket.projectName || "—"}
                    </p>
                    {canEditProject && ticket.status === "New" && (
                      <button
                        onClick={() => setShowProjectEdit(true)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit Project"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2 items-center">
                    <select
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      className="border rounded-md px-2 py-1 text-sm w-full"
                    >
                      <option value="">Select Project</option>
                      {projects.map((p) => (
                        <option key={p.projectUID} value={p.projectUID}>
                          {p.projectName}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => handleProjectUpdate()}
                      className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>

                    <button
                      onClick={() => setShowProjectEdit(false)}
                      className="text-gray-500 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <DetailCard
                label="Raised By Email"
                value={ticket.raisedByEmail || "—"}
                icon={Mail}
              />

              <DetailCard
                label="Raised At"
                value={formatDateTime(ticket.raisedAt)}
                icon={Timer}
              />

              <DetailCard
                label="Assigned By"
                value={ticket.assignedBy || "—"}
                icon={UserCheck}
              />

              <DetailCard
                label="Assigned To"
                value={ticket.assignedTo || "—"}
                icon={UserPlus}
              />

              <DetailCard
                label="Estimated Completion"
                value={formatDateTime(ticket.estimatedCompleteByDev)}
                icon={Timer}
              />

              <DetailCard
                label="Approved Date"
                value={formatDateTime(ticket.approvedAt)}
                icon={CalendarDays}
              />
            </div>

            {/* ATTACHMENTS */}
            {ticket.attachmentUrls?.length > 0 && (
              <div className="mt-6 p-5 bg-white rounded-xl border shadow-sm">
                <h3 className="text-gray-800 font-semibold mb-4">
                  Attachments:
                </h3>

                <div className="flex flex-wrap gap-3">
                  {ticket.attachmentUrls.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setPreviewUrl(url)}
                      className="px-6 py-2 text-sm bg-gradient-to-r 
          from-[#0061A4] to-[#00ACED] 
          text-white rounded-md shadow-md 
          hover:opacity-90 transition"
                    >
                      Attachment {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ACTION BUTTONS */}
            {ticket.status !== "Closed" && (
              <div className="mt-2 p-5 bg-white rounded-xl border shadow-sm">
                <h3 className="text-gray-800 font-semibold mb-4">
                  Available Actions
                </h3>

                {/* NEW */}
                {ticket.status === "New" && (
                  <div className="flex gap-3 flex-wrap">
                    <button onClick={() => onApprove(ticket)} className={btn}>
                      Approve
                    </button>
                    <button onClick={() => onReject(ticket)} className={btn}>
                      Reject
                    </button>
                    <button onClick={() => onAssign(ticket)} className={btn}>
                      Assign
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* APPROVED */}
                {ticket.status === "Approved" && (
                  <div className="flex gap-3 flex-wrap">
                    <button onClick={() => onAssign(ticket)} className={btn}>
                      Assign
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                    <button onClick={() => onReject(ticket)} className={btn}>
                      Reject
                    </button>
                  </div>
                )}

                {/* ASSIGNED */}
                {ticket.status === "Assigned" && (
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => onInProgress(ticket)}
                      className={btn}
                    >
                      In Progress
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* IN PROGRESS */}
                {ticket.status === "In Progress" && (
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => onReadyForReview(ticket)}
                      className={btn}
                    >
                      Ready for Review
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* READY FOR REVIEW */}
                {ticket.status === "Ready For Review" && (
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => onAssignToReviewer(ticket)}
                      className={btn}
                    >
                      Assign to Reviewer
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* ASSIGN TO REVIEWER */}
                {ticket.status === "Assign To Reviewer" && (
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => onReviewInProgress(ticket)}
                      className={btn}
                    >
                      Review In Progress
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* REVIEW IN PROGRESS */}
                {ticket.status === "Review In Progress" && (
                  <div className="flex gap-3 flex-wrap">
                    <button onClick={() => onReviewed(ticket)} className={btn}>
                      Reviewed
                    </button>
                    <button onClick={() => onReopen(ticket)} className={btn}>
                      Re-open
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* REVIEWED */}
                {ticket.status === "Reviewed" && (
                  <div className="flex gap-3 flex-wrap">
                    <button onClick={() => onClosed(ticket)} className={btn}>
                      Close
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* RE-OPEN */}
                {ticket.status === "Re-open" && (
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => onInProgress(ticket)}
                      className={btn}
                    >
                      In Progress
                    </button>
                    <button onClick={() => onHold(ticket)} className={btn}>
                      Hold
                    </button>
                  </div>
                )}

                {/* HOLD */}
                {ticket.status === "Hold" && (
                  <div className="flex gap-3 flex-wrap">
                    <button onClick={() => onResume(ticket)} className={btn}>
                      Resume
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="p-4 bg-gray-100 border-t flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm bg-white border rounded-md hover:bg-gray-200 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <AttachmentPreviewModal
        show={!!previewUrl}
        url={previewUrl}
        onClose={() => setPreviewUrl(null)}
      />

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={3000}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ViewTicketModal;

/* Detail card */
const DetailCard = ({ label, value, icon: Icon, chip, severityChip }) => (
  <div className="p-4 bg-gray-50 border rounded-xl shadow-sm hover:shadow-md transition-all">
    <p className="text-gray-500 text-sm mb-1 flex items-center gap-2">
      {Icon && <Icon size={16} className="text-[#0061A4]" />}
      {label}
    </p>

    {!chip && !severityChip && (
      <p className="text-gray-800 font-medium break-words max-w-full">
        {value}
      </p>
    )}

    {chip && (
      <span className="inline-block px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium break-words max-w-full">
        {value}
      </span>
    )}

    {severityChip && (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium break-words max-w-full ${
          value === "High"
            ? "bg-red-100 text-red-700"
            : value === "Moderate"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-green-100 text-green-700"
        }`}
      >
        {value}
      </span>
    )}
  </div>
);
